/**
 * Navigation feedback: a thin bar across the top from the moment a link is tapped until the next page appears.
 * The store is multi-page, so the old page stays on screen while the next one loads, and nothing showed the tap had
 * registered (shoppers tap again, or give up).
 *
 * Only for real page changes nothing else has handled: a same-origin link or GET/POST form, left click / tap without
 * modifier keys, not a new tab, not a download, not a link to the same page (or one of its #anchors), and not a click
 * or submit the theme has already taken over (filters, drawers, add to bag call preventDefault). The listeners sit on
 * window in the bubble phase, so they run after every other handler and see that decision.
 *
 * The bar appears after a short delay (CSS), so a page that arrives almost at once (prefetched) shows nothing; it eases
 * towards 90 % and never completes on its own: the next page replaces it. A page restored from the back/forward cache
 * clears it, and it gives up after 15 s if no navigation followed (a stopped load, a file download).
 * Styles: swagchic.css (.sc-nav-progress). Opt out per link or form with data-no-progress.
 * Ported from MLB's assets/mlb-nav-progress.js (21 Oct 2026).
 */
const GIVE_UP_AFTER = 15000;

/** @type {HTMLDivElement | null} */
let bar = null;
/** @type {number | undefined} */
let giveUp;

function start() {
  if (!bar) {
    bar = document.createElement('div');
    bar.className = 'sc-nav-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.append(bar);
  }
  bar.classList.remove('is-active');
  void bar.offsetWidth; // restart the animation for a second tap
  bar.classList.add('is-active');
  clearTimeout(giveUp);
  giveUp = window.setTimeout(stop, GIVE_UP_AFTER);
}

function stop() {
  clearTimeout(giveUp);
  bar?.classList.remove('is-active');
}

/**
 * @param {URL} url
 * @returns {boolean} true when the URL loads another page of this store
 */
function isOtherPage(url) {
  if (url.origin !== location.origin) return false; // other sites, mailto:, tel:, javascript:
  return url.pathname !== location.pathname || url.search !== location.search; // same page + #anchor: no load
}

window.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const target = event.target instanceof Element ? event.target : null;
  if (!target || target.closest('[data-no-progress]')) return;

  // A product card tap outside its links: product-card.js (navigateToProduct) opens the product page by script, with
  // the same rule as here: not in the theme editor's no-navigation cards, not on a button, input, label or select
  const card = target.closest('product-card');
  if (card && !target.closest('a[href]')) {
    if (card.hasAttribute('data-no-navigation') || target.closest('button, input, label, select, [tabindex="1"]')) return;
    const cardLink = card.querySelector('[ref="productCardLink"]');
    if (cardLink instanceof HTMLAnchorElement && cardLink.href && isOtherPage(new URL(cardLink.href, location.href))) start();
    return;
  }

  const anchor = target.closest('a[href]');
  if (!(anchor instanceof HTMLAnchorElement)) return;
  if ((anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;
  if (isOtherPage(new URL(anchor.href, location.href))) start();
});

window.addEventListener('submit', (event) => {
  if (event.defaultPrevented) return;
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.closest('[data-no-progress]')) return;
  const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
  const target = submitter?.getAttribute('formtarget') || form.target;
  if (target && target !== '_self') return;
  const action = new URL(submitter?.getAttribute('formaction') || form.getAttribute('action') || location.href, location.href);
  if (action.origin === location.origin) start();
});

// Back/forward cache: the page comes back exactly as it was left, bar included
window.addEventListener('pageshow', (event) => {
  if (event.persisted) stop();
});

// iPhone Safari only applies :active (the pressed state in swagchic.css) when the page listens for touches
document.addEventListener('touchstart', () => {}, { passive: true });
